import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';

/**
 * Admin KYC Template Management (v1.18.0) — independent from the investor-
 * facing endpoints in kycApi.js, gated by the `manage-kyc-documents` role
 * check (see RoleRoute/Sidebar wiring — same admin/super-admin role check
 * every other admin gate in this app uses, since there is no granular
 * `permissions` array anywhere on the client-side user object to check
 * against instead; see selectUserRoles).
 *
 * IMPORTANT — mostly unverified against a live response: the mockup page
 * (`/frontend/rollover-kyc-screens.html`) was unreachable this session
 * (network-allowlist 403 on the backend host, and a 180s timeout on the
 * in-app browser — see project notes), so most URLs/body fields/response
 * shapes below are still this file's best-effort construction from the
 * spec's prose, following this codebase's existing REST conventions
 * (versioned-sub-resource for "add a new version" rather than
 * PUT-in-place, since old versions are explicitly never deleted). One field
 * IS now confirmed live: POST /admin/kyc/templates 422'd with
 * `{ errors: { title: ["The title field is required."] } }`, so the
 * create-template display-name field is `title`, not the originally
 * assumed `label` (fixed in normalizeTemplate below + KycTemplates.jsx's
 * create form). Everything else here is still unconfirmed and should be
 * checked the same way — every parser is written defensively (never
 * throws, logs + flags `parseError` on an unrecognized shape) for exactly
 * that reason, same pattern as kycApi.js's unwrapSubmissionsPage.
 *
 * Endpoints (❓ = still unconfirmed, ✅ = confirmed live):
 *   GET    /admin/kyc/templates                 ❓ list (current version only, per tag)
 *   GET    /admin/kyc/templates/{tag}            ❓ detail + full version history
 *   POST   /admin/kyc/templates                  ✅ path+method; ❓ full field list beyond `title` — multipart tag+title+file assumed
 *   POST   /admin/kyc/templates/{tag}/versions    ❓ replace/supersede: multipart file(+title)
 *   DELETE /admin/kyc/templates/{tag}             ❓ soft-delete current version
 *
 * Soft-delete semantics (explicit, per spec): deleting a template does NOT
 * remove the file or its version history — it drops the tag out of every
 * investor's "Other Documents" checklist and out of the completion-
 * percentage denominator going forward. Existing uploads against that tag
 * are untouched. The confirm dialog for this action must say exactly that,
 * not a generic "are you sure?" (see pages/admin/KycTemplates.jsx).
 */
function normalizeTemplateVersion(raw) {
  if (!raw) return raw;
  return {
    id: raw.uuid ?? raw.id,
    versionNumber: raw.version_number ?? raw.version ?? null,
    fileName: raw.original_name ?? raw.filename ?? 'template',
    downloadUrl: raw.download_url ?? raw.url ?? null,
    isCurrent: !!(raw.is_current ?? raw.current),
    uploadedBy: raw.uploaded_by?.name ?? raw.uploaded_by?.full_name ?? raw.created_by?.name ?? null,
    uploadedAt: raw.created_at ?? raw.uploaded_at ?? null,
  };
}

function normalizeTemplate(raw) {
  if (!raw) return raw;
  const currentVersion = normalizeTemplateVersion(raw.current_version ?? raw.currentVersion ?? raw);
  return {
    tag: raw.tag ?? null,
    // `title` is the confirmed wire field (see file header); `label`/`name`
    // kept as fallbacks in case a different endpoint on this same resource
    // (e.g. the list/detail GETs, still unconfirmed) uses different naming.
    label: raw.title ?? raw.label ?? raw.name ?? raw.tag ?? 'Template',
    description: raw.description ?? null,
    currentVersion,
    versionCount: raw.version_count ?? raw.versions?.length ?? (currentVersion ? 1 : 0),
    versions: Array.isArray(raw.versions) ? raw.versions.map(normalizeTemplateVersion) : currentVersion ? [currentVersion] : [],
    isDeleted: !!(raw.deleted_at ?? raw.is_deleted),
    createdAt: raw.created_at ?? null,
    updatedAt: raw.updated_at ?? null,
  };
}

function unwrapTemplateList(response) {
  const list = response?.data ?? response;
  if (Array.isArray(list)) return { items: list, parseError: false };
  // eslint-disable-next-line no-console
  console.error('[kycTemplatesApi] Unexpected GET /admin/kyc/templates response shape. Got:', response);
  return { items: [], parseError: true };
}

function unwrapTemplate(response) {
  const candidate = response?.template ?? response?.data ?? response;
  const looksLikeTemplate = candidate && typeof candidate === 'object' && ('tag' in candidate);
  if (!looksLikeTemplate) {
    // eslint-disable-next-line no-console
    console.error('[kycTemplatesApi] Unexpected single-template response shape. Got:', response);
    return null;
  }
  return candidate;
}

export const kycTemplatesApi = createApi({
  reducerPath: 'kycTemplatesApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['KycTemplate'],
  endpoints: (builder) => ({
    // GET /admin/kyc/templates — list, current version per tag, including
    // soft-deleted tags (the management screen still needs to show/restore-
    // reason about those; the investor-facing list at
    // kycApi.js's listInvestorKycTemplates only ever returns live ones).
    adminListKycTemplates: builder.query({
      query: () => ({ url: '/admin/kyc/templates', method: 'GET' }),
      transformResponse: (response) => {
        const { items, parseError } = unwrapTemplateList(response);
        return { templates: items.map(normalizeTemplate), parseError };
      },
      providesTags: (result) => [
        ...(result?.templates ?? []).map((t) => ({ type: 'KycTemplate', id: t.tag })),
        { type: 'KycTemplate', id: 'LIST' },
      ],
    }),

    // GET /admin/kyc/templates/{tag} — detail + full version history, for
    // the version-history view (structural precedent: RolloverHistory.jsx).
    adminGetKycTemplate: builder.query({
      query: (tag) => ({ url: `/admin/kyc/templates/${tag}`, method: 'GET' }),
      transformResponse: (response) => normalizeTemplate(unwrapTemplate(response)),
      providesTags: (result, error, tag) => [{ type: 'KycTemplate', id: tag }],
    }),

    // POST /admin/kyc/templates — multipart: tag, label, file. A 422 here
    // (tag already has a current template, or an entirely unrecognized tag
    // if the backend does enforce a stricter enum than the two known
    // examples) is surfaced via authErrorMessage at the call site rather
    // than pre-validated away, per the user's explicit "let the 422 handle
    // it" fallback for tags this app doesn't know about in advance.
    adminCreateKycTemplate: builder.mutation({
      query: (formData) => ({ url: '/admin/kyc/templates', method: 'POST', data: formData }),
      transformResponse: (response) => normalizeTemplate(unwrapTemplate(response)),
      invalidatesTags: [{ type: 'KycTemplate', id: 'LIST' }],
    }),

    // POST /admin/kyc/templates/{tag}/versions — replace/supersede: uploads
    // a new current version, previous version(s) retained in history, never
    // deleted. This never triggers any investor-facing "please resubmit"
    // prompt — that's explicitly forbidden by the spec.
    adminReplaceKycTemplate: builder.mutation({
      query: ({ tag, formData }) => ({ url: `/admin/kyc/templates/${tag}/versions`, method: 'POST', data: formData }),
      transformResponse: (response) => normalizeTemplate(unwrapTemplate(response)),
      invalidatesTags: (result, error, { tag }) => [
        { type: 'KycTemplate', id: tag },
        { type: 'KycTemplate', id: 'LIST' },
      ],
    }),

    // DELETE /admin/kyc/templates/{tag} — soft-delete of the CURRENT
    // version only. History and the file itself are retained server-side;
    // this just drops the tag from investors' checklists and the
    // completion-percentage denominator going forward. See file header.
    adminDeleteKycTemplate: builder.mutation({
      query: (tag) => ({ url: `/admin/kyc/templates/${tag}`, method: 'DELETE' }),
      invalidatesTags: (result, error, tag) => [
        { type: 'KycTemplate', id: tag },
        { type: 'KycTemplate', id: 'LIST' },
      ],
    }),
  }),
});

export const {
  useAdminListKycTemplatesQuery,
  useAdminGetKycTemplateQuery,
  useAdminCreateKycTemplateMutation,
  useAdminReplaceKycTemplateMutation,
  useAdminDeleteKycTemplateMutation,
} = kycTemplatesApi;
